/**
 * The signed-in frame: top bar + left menu + main area, exactly the prototype's `.top`, `.shell`, `.side`,
 * `.main > .inner`. The menu is GET /me → `menu`, rendered as given: item order, labels, icons, group
 * headings, badges and hub tabs all come from the server (ADR §11.2). No role or person literal here.
 *
 * On a phone the same `.side` becomes the bottom menu sheet and hub tabs become the scrolling tab row
 * (prototype CSS, max-width 760px).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useParams, useSearchParams } from 'react-router';
import { ROLE_LABEL, isRequesterRole, type MenuItem } from '@amplispot/contracts';
import { useSession } from '../lib/session';
import { Icon } from '../ui/Icon';
import { CRUMB_GROUP } from './copy';
import { useSignOut } from './sign-out';
import { ActingBanner } from './ActingBanner';
import { ZohoPill } from './ZohoPill';
import { GuideButton, HelpProvider, WelcomeBanner } from './Help';
import { HELP } from './help-content';
import { readPref, writePref } from './prefs';
import { TaskPanel } from '../screens/work/TaskPanel';
import { ItemHost } from '../screens/requests/ItemPanels';
import { BriefHost } from '../screens/clients/ClientBrief';

export function AppShell() {
  return (
    <HelpProvider>
      <Frame />
    </HelpProvider>
  );
}

function Frame() {
  const { item: itemKey, tab: tabKey } = useParams();
  const { me } = useSession();
  const userId = me.user.id;
  // The collapsed menu is remembered per person, in this browser (desktop only; a phone uses the bottom menu).
  const [collapsed, setCollapsed] = useState(() => readPref('nav.collapsed', userId) === '1');
  const mainRef = useRef<HTMLElement>(null);
  const loc = useLocation();

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [loc.pathname]);

  const toggle = () => {
    setCollapsed((c) => {
      writePref('nav.collapsed', userId, c ? '0' : '1');
      return !c;
    });
  };

  const active = me.menu.find((m) => m.key === itemKey);
  // The client deck (/clients/deck, prototype `deck`) is not one of the Clients tabs: Clients is lit, without its subnav.
  const clientDeck = itemKey === 'clients' && tabKey === 'deck';
  const activeTab = clientDeck ? undefined : active?.tabs.find((t) => t.key === tabKey) ?? active?.tabs[0];
  // The crumb names the screen: the tab's own name ("All work", "My day"), or the item when it has no tabs.
  // While the timesheet gate is up every route renders the catch-up (Page.tsx), so the crumb names it, not the route.
  const gated = !!me.gate?.timesheet_blocked;
  const clientUsage = itemKey === 'usage' && tabKey === 'client';
  const crumbTitle = gated ? HELP.catchup!.t : itemKey === 'capture' ? 'Capture' : clientUsage && active ? 'Client usage' : clientDeck && active ? 'Client deck' : active ? (activeTab ? activeTab.label : active.label) : '';
  const crumbGroup = gated ? CRUMB_GROUP.catchup! : itemKey === 'capture' ? CRUMB_GROUP.capture! : active ? (CRUMB_GROUP[active.key] ?? 'Ops') : 'Ops';

  return (
    <>
      <TopBar crumbGroup={crumbGroup} crumbTitle={crumbTitle} />
      <ActingBanner />
      <div className={`shell${collapsed ? ' collapsed' : ''}`} id="shell">
        <nav className="side" id="side" aria-label="Main menu" style={me.gate?.timesheet_blocked ? { opacity: 0.4, pointerEvents: 'none' } : undefined} aria-disabled={me.gate?.timesheet_blocked || undefined}>
          {me.menu.map((m, i) => (
            <NavItem key={m.key} item={m} heading={groupHeading(me.menu, i)} active={m.key === itemKey} activeTab={activeTab?.key} subnav={!clientDeck} />
          ))}
          <button type="button" className="collapse" onClick={toggle} aria-pressed={collapsed} aria-label={collapsed ? 'Expand the menu' : 'Collapse to icons'} title={collapsed ? 'Expand the menu' : 'Collapse to icons'}>
            <Icon name="left" />
            <span>Collapse to icons</span>
          </button>
        </nav>
        <main className="main" ref={mainRef}>
          <div className="inner" id="view">
            <WelcomeBanner />
            <Outlet />
          </div>
        </main>
      </div>
      <TaskHost />
      <ItemHost />
      <BriefHost />
    </>
  );
}

/**
 * The task side panel, on any page: every Work row and Today exception routes to `?task=<id>`
 * (contract `route`), so the panel opens over whatever screen the person is on and closing it keeps them there.
 */
function TaskHost() {
  const [sp, setSp] = useSearchParams();
  const id = sp.get('task');
  if (!id) return null;
  const close = () => {
    const n = new URLSearchParams(sp);
    n.delete('task');
    setSp(n);
  };
  return <TaskPanel key={id} id={id} onClose={close} />;
}

/**
 * The group heading to draw above menu item `i`, or null. A heading opens a group, so it is drawn only when the
 * item's group differs from the group already open (the last non-null group above it): founder's Settings and
 * Holiday calendar are both Admin and share one heading; HR's lone Holiday calendar still gets its own.
 */
function groupHeading(menu: readonly MenuItem[], i: number): string | null {
  const g = menu[i]!.group;
  if (!g) return null;
  for (let j = i - 1; j >= 0; j--) {
    const prev = menu[j]!.group;
    if (prev) return prev === g ? null : g;
  }
  return g;
}

function NavItem({
  item,
  heading,
  active,
  activeTab,
  subnav = true,
}: {
  item: MenuItem;
  heading: string | null;
  active: boolean;
  activeTab: string | undefined;
  /** False on a page reached from a hub but not one of its tabs (prototype: the deck lights Clients, no subnav). */
  subnav?: boolean;
}) {
  return (
    <>
      {heading ? <div className="grp">{heading}</div> : null}
      <Link className={`nav${active ? ' on' : ''}`} to={item.route} title={item.label} aria-current={active ? 'page' : undefined}>
        <Icon name={item.icon} />
        <span>{item.label}</span>
        {item.badge ? (
          <em className={`c${item.badge.tone === 'alert' ? ' r' : ''}`} style={{ fontStyle: 'normal' }}>
            {item.badge.count}
          </em>
        ) : null}
      </Link>
      {active && subnav && item.tabs.length > 1 ? (
        <div className="subnav">
          {item.tabs.map((t) => (
            <Link key={t.key} className={`nav sn${t.key === activeTab ? ' on' : ''}`} to={t.route} aria-current={t.key === activeTab ? 'page' : undefined}>
              <span>{t.label}</span>
              {t.badge ? (
                <em className={`c${t.badge.tone === 'alert' ? ' r' : ''}`} style={{ fontStyle: 'normal' }}>
                  {t.badge.count}
                </em>
              ) : null}
            </Link>
          ))}
        </div>
      ) : null}
    </>
  );
}

function TopBar({ crumbGroup, crumbTitle }: { crumbGroup: string; crumbTitle: string }) {
  const { me, can } = useSession();
  const [open, setOpen] = useState(false);
  // Leaves only once the server ended the session (or 401); otherwise stays and toasts SIGN_OUT_FAILED.
  const { signOut, signingOut } = useSignOut();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  // The "Needs you" pill counts the caller's home list; it shows once that list has something in it.
  const home = me.menu.find((m) => m.route === me.home_route);
  // Sales / Marketing (requests only) never see it — prototype updateTop hides #needPill for the requester.
  const need = isRequesterRole(me.user.role) ? null : (home?.badge ?? null);

  return (
    <header className="top">
      <Link to={me.home_route} className="logo" style={{ textDecoration: 'none' }} aria-label="Amplispot Ops home">
        <div className="glyph">
          <i />
          <i />
          <i />
          <i />
        </div>
        <div>
          <div className="lg1">Amplispot</div>
          <div className="lg2">Ops</div>
        </div>
      </Link>
      <div className="crumb" id="crumb">
        <span className="cl">{crumbGroup}</span>
        <b>{crumbTitle}</b>
      </div>
      <div className="sp" />
      {/* Prototype order: the Guide button is inserted before Capture (insertAdjacentHTML('beforebegin') on #capBtn). */}
      <GuideButton />
      {can('capture.use') && !me.gate?.timesheet_blocked ? (
        <Link className="btn pri" id="capBtn" to="/capture" style={{ textDecoration: 'none' }} aria-label="Capture">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
            <path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4" />
          </svg>
          Capture
        </Link>
      ) : null}
      {need ? (
        <Link className="pill need" id="needPill" to={me.home_route} style={{ textDecoration: 'none' }}>
          Needs you <span className="n">{need.count}</span>
        </Link>
      ) : null}
      {can('money.read') ? <ZohoPill /> : null}
      <div className="user" ref={ref}>
        <span className="av">{me.user.initials}</span>
        <div>
          <div className="nm">{me.user.name}</div>
          <button className="who" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
            {ROLE_LABEL[me.user.role]}
            {isRequesterRole(me.user.role) ? ' (requests only)' : null}
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </div>
        {open ? (
          <div className="umenu" role="menu">
            <div className="um-h">
              <b>{me.user.name}</b>
              <small>{me.user.email}</small>
            </div>
            <button role="menuitem" onClick={signOut} disabled={signingOut} aria-busy={signingOut}>
              <Icon name="logout" />
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
